<script setup>
import { computed, ref } from 'vue'
import { scaleBand, scaleLinear } from 'd3-scale'
import { max } from 'd3-array'
import { useD3Container } from '@/composables/useD3Chart'

const props = defineProps({
  // [{ label: '2021', value: 14, raw: 2021 }, ...]
  data:     { type: Array, default: () => [] },
  height:   { type: Number, default: 130 },
  color:    { type: String, default: '#3b82f6' },
  // Raw value of the active filter — that bar is highlighted, others dimmed
  selected: { type: [String, Number], default: null },
})

const emit = defineEmits(['bar-click'])

const wrapEl   = ref(null)
const width    = ref(0)
const hoverIdx = ref(null)

useD3Container(wrapEl, () => {
  width.value = wrapEl.value?.clientWidth ?? 0
})

const MARGIN = { top: 14, bottom: 18 }

const bars = computed(() => {
  if (!width.value || !props.data.length) return []
  const x = scaleBand()
    .domain(props.data.map(d => d.label))
    .range([0, width.value])
    .paddingInner(0.25)
    .paddingOuter(0.1)
  const innerH = props.height - MARGIN.top - MARGIN.bottom
  const y = scaleLinear().domain([0, max(props.data, d => d.value) || 1]).range([0, innerH])
  // Skip x labels when bands get too narrow
  const labelEvery = x.bandwidth() >= 26 ? 1 : Math.ceil(28 / (x.bandwidth() + x.step() - x.bandwidth()))
  return props.data.map((d, idx) => ({
    item: d,
    idx,
    x: x(d.label),
    w: x.bandwidth(),
    h: Math.max(y(d.value), 1),
    yTop: MARGIN.top + innerH - Math.max(y(d.value), 1),
    showLabel: idx % labelEvery === 0 || props.data.length <= 8,
  }))
})

function isSelected(item) {
  return props.selected != null && item.raw === props.selected
}

function isDimmed(b) {
  if (hoverIdx.value !== null) return hoverIdx.value !== b.idx
  return props.selected != null && !isSelected(b.item)
}
</script>

<template>
  <div ref="wrapEl" class="bar-wrap">
    <div v-if="!data.length" class="bar-empty">—</div>
    <svg v-else class="bar-svg" :width="width" :height="height">
      <g
        v-for="b in bars"
        :key="b.item.label"
        class="bar-group"
        @mouseenter="hoverIdx = b.idx"
        @mouseleave="hoverIdx = null"
        @click="emit('bar-click', b.item)"
      >
        <title>{{ b.item.label }}: {{ b.item.value.toLocaleString() }}</title>
        <!-- full-height hit area so narrow bars stay clickable -->
        <rect :x="b.x" y="0" :width="b.w" :height="height" fill="transparent" />
        <rect
          class="bar-rect"
          :class="{ 'bar-rect--dim': isDimmed(b), 'bar-rect--selected': isSelected(b.item) }"
          :x="b.x"
          :y="b.yTop"
          :width="b.w"
          :height="b.h"
          :fill="color"
          rx="2"
        />
        <text
          v-if="hoverIdx === b.idx || isSelected(b.item)"
          class="bar-value"
          :x="b.x + b.w / 2"
          :y="b.yTop - 4"
          text-anchor="middle"
        >{{ b.item.value.toLocaleString() }}</text>
        <text
          v-if="b.showLabel"
          class="bar-label"
          :x="b.x + b.w / 2"
          :y="height - 5"
          text-anchor="middle"
        >{{ b.item.label }}</text>
      </g>
    </svg>
  </div>
</template>

<style scoped>
.bar-wrap {
  width: 100%;
  min-width: 0;
}
.bar-empty {
  text-align: center;
  padding: 18px 0;
  color: var(--p-text-color-secondary);
  font-size: .85rem;
}
.bar-svg { display: block; }
.bar-group { cursor: pointer; }
.bar-rect { transition: opacity .15s ease; }
.bar-rect--dim { opacity: 0.3; }
.bar-rect--selected {
  stroke: var(--p-text-color);
  stroke-width: 1.5;
}
.bar-value {
  fill: var(--p-text-color);
  font-size: 10px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.bar-label {
  fill: var(--p-text-color-secondary);
  font-size: 9px;
}
</style>
